import { React, useState, useEffect, useContext, useRef, useMemo } from 'react';
import UserContext from '../../../components/User_Context.js';
import { dms_style } from "../../../styles/style.js";
import * as menu from "../../../assets/menus/menu.js";
import * as api from "../../../api/apiConnector.js";
import Loader from '../../../components/ui/loader.js';
import Choices from './../../../components/Choices.js';
import { useTranslation } from 'react-i18next';
import { UncontrolledPopover, PopoverHeader, PopoverBody } from 'reactstrap';
import { RightsTreeEnum } from '../../../assets/menus/rightsTreeEnum.js';
import { handHygieneLabels } from '../../_label/hand_hygiene/hand_hygiene_labels.js';
import * as breadcrumb from "../../../components/ui/breadcrumb.js";

function Hand_Hygiene_Finder(params) {
    // get current user from context
    const { currentUser } = useContext(UserContext);

    // Results and vocabularies
    const [searchList, setSearchList] = useState(null);
    const [departmentsVoc, setDepartmentsVoc] = useState([]);
    const [functionalAreaVoc, setFunctionalAreaVoc] = useState([]);
    const [filteredFunctionalArea, setFilteredFunctionalArea] = useState([]);
    const [handHygieneDepartmentsCategoryVoc, setHandHygieneDepartmentsCategoryVoc] = useState([]);

    // API filters (top panel)
    const [observer, setObserver] = useState("");
    const [department, setDepartment] = useState([]);
    const [departmentCategory, setDepartmentCategory] = useState([]);
    const [functionalArea, setFunctionalArea] = useState([]);
    const [startObservationDate, setStartObservationDate] = useState("");
    const [endObservationDate, setEndObservationDate] = useState("");
    const [indexId, setIndexId] = useState("");

    // Table filters
    const [tableObserverFilter, setTableObserverFilter] = useState("");
    const [tableDateFilter, setTableDateFilter] = useState("");
    const [tableDepartmentFilter, setTableDepartmentFilter] = useState([]);
    const [tableDeptCategoryFilter, setTableDeptCategoryFilter] = useState([]);
    const [tableFunctionalAreaFilter, setTableFunctionalAreaFilter] = useState([]);
    const [tableNotesFilter, setTableNotesFilter] = useState("");
    const [tableAcpMFilter, setTableAcpMFilter] = useState("");
    const [tableOppFilter, setTableOppFilter] = useState("");
    const [tableIdFilter, setTableIdFilter] = useState("");

    // Refs to clear Choices components
    const departmentRef = useRef();
    const departmentCategoryRef = useRef();
    const functionalAreaRef = useRef();

    // Labels using centralized translations
    const { t } = useTranslation();

    // Set breadcrumb using centralized component
    const setBreadCrumb = () => {
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/hand_hygiene/finder", name: t('handHygieneDataFinder', { ns: 'labels' }) }
        ];
        breadcrumb.set(path);
    };

    // Initialize translations and load vocabularies on mount
    useEffect(() => {
        (async () => {
            // First initialize translations
            const i18n = menu.getI18n();
            i18n.addResourceBundle('it', 'labels', handHygieneLabels.it, true, true);
            i18n.addResourceBundle('en', 'labels', handHygieneLabels.en, true, true);

            // Then set breadcrumb with translations available
            setBreadCrumb();
            menu.write({ user: currentUser() });
            await loadVocs();
        })();
    }, []);

    // Recursively get child units
    const getDescendants = (list, parentId) => {
        const directChildren = list.filter(x => x.ParentId === parentId);
        return directChildren.flatMap(child => [child, ...getDescendants(list, child.Id)]);
    };

    // Load business units and categories
    const loadVocs = async () => {
        const bu = (await api.sendMessage("admin/system/organization/get_business_units", {
            user: currentUser(),
        }))?.units ?? [];
        const deps = bu.filter(x => x.UnitTypeCode === 'DEP');
        setDepartmentsVoc(deps);
        const fas = bu.filter(x => x.UnitTypeCode === 'OPUNIT');
        setFunctionalAreaVoc(fas);
        setFilteredFunctionalArea(fas);
        const cat = (await api.sendMessage("admin/vocs/get", {
            user: currentUser(),
            body: { voc_id: "hand_hygiene_oms_departments_category" }
        }))?.items ?? [];
        setHandHygieneDepartmentsCategoryVoc(cat);
    };

    // Fetch filtered data from API
    const getFilteredData = async () => {

        //Clear all filters
        clearTableFilters();
        const result = await api.sendMessage("darm/handHygiene/getFilteredSheetData", {
            user: currentUser(),
            body: {
                observer,
                department,
                departmentType: departmentCategory,
                functionalArea,
                startObservationDate,
                endObservationDate,
                indexId
            }
        });
        const sorted = (result.filtered ?? []).sort((a, b) => {
            const dateA = new Date(a.Date);
            const dateB = new Date(b.Date);

            if (dateA.getTime() !== dateB.getTime()) {
                return dateB - dateA; // Date (dec)
            }

            const startA = new Date(a.StartObservation);
            const startB = new Date(b.StartObservation);

            if (startA.getTime() !== startB.getTime()) {
                return startA - startB; // Start Observation (cres)
            }

            return (a.SequentialNumber ?? 0) - (b.SequentialNumber ?? 0); // ID (cres)
        });

        setSearchList(sorted);
    };

    // Clear top panel filters
    const clearData = () => {
        setObserver("");
        setDepartment([]);
        setDepartmentCategory([]);
        setFunctionalArea([]);
        setStartObservationDate("");
        setEndObservationDate("");
        setIndexId("");
        setSearchList(null);
        setFilteredFunctionalArea(functionalAreaVoc);
        departmentRef.current?.clearSelection();
        departmentCategoryRef.current?.clearSelection();
        functionalAreaRef.current?.clearSelection();
    };

    // Clear table filters only
    const clearTableFilters = () => {
        setTableObserverFilter('');
        setTableDateFilter('');
        setTableDepartmentFilter([]);
        setTableDeptCategoryFilter([]);
        setTableFunctionalAreaFilter([]);
        setTableNotesFilter('');
        setTableAcpMFilter('');
        setTableOppFilter('');
        setTableIdFilter('');
    };

    // Format ISO date to dd/MM/yyyy
    const formatDate = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    // Get department category name from code
    const getDepartmentCategoryName = (code) => {
        if (!code) return "";
        const category = handHygieneDepartmentsCategoryVoc.find(cat => cat.Code === code);
        return category ? category.Name : code;
    };

    // Pagination state
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);

    // Client-side table filtering
    const displayedList = useMemo(() => {
        if (!searchList) return [];
        return searchList.filter(item => {
            // observer
            if (tableObserverFilter && !item.ObserverFullName.toLowerCase().includes(tableObserverFilter.toLowerCase())) return false;
            // date exact
            if (tableDateFilter) {
                const iso = new Date(item.Date).toISOString().slice(0, 10);
                if (iso !== tableDateFilter) return false;
            }
            // department
            if (tableDepartmentFilter.length) {
                if (!tableDepartmentFilter.includes(item.DepartmentName)) return false;
            }
            // departmentCategory
            if (tableDeptCategoryFilter.length) {
                const categoryName = getDepartmentCategoryName(item.DepartmentType);
                if (!tableDeptCategoryFilter.includes(categoryName)) return false;
            }
            // functionalArea
            if (tableFunctionalAreaFilter.length) {
                if (!tableFunctionalAreaFilter.includes(item.FunctionalAreaName)) return false;
            }
            // notes yes/no
            if (tableNotesFilter) {
                const val = item.Notes ? 'yes' : 'no';
                if (val !== tableNotesFilter) return false;
            }
            // acpm
            if (tableAcpMFilter) {
                const val = item.Acpm ? 'yes' : 'no';
                if (val !== tableAcpMFilter) return false;
            }
            // opp count
            if (tableOppFilter) {
                if (String(item.OpportunityCount) !== String(tableOppFilter)) return false;
            }
            if (tableIdFilter) {
                if (String(item.SequentialNumber) !== String(tableIdFilter)) return false;
            }
            return true;
        });
    }, [
        searchList,
        tableObserverFilter, tableDateFilter,
        tableDepartmentFilter, tableDeptCategoryFilter, tableFunctionalAreaFilter,
        tableNotesFilter, tableAcpMFilter, tableOppFilter, tableIdFilter
    ]);

    // Slice for pagination
    const paginatedList = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return displayedList.slice(start, start + itemsPerPage);
    }, [displayedList, currentPage, itemsPerPage]);


    // Render a row
    const getTableRow = (item) => (
        <tr
            key={item.Id}
            style={{
                ...dms_style.table_row,
                cursor: 'pointer'
            }}
            onClick={() => window.location.href = `/darm/handHygiene/${item.Id}`}
        >
            <td
                className="text-center"
                style={{
                    whiteSpace: 'nowrap',
                    minWidth: '60px'
                }}
            >
                {item.SequentialNumber}
            </td>
            <td
                style={{
                    whiteSpace: 'nowrap',
                    minWidth: '100px'
                }}
            >
                {formatDate(item.Date)}
            </td>
            <td
                style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '180px',
                    minWidth: '120px'
                }}
            >
                {item.ObserverFullName}
            </td>
            <td
                style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '180px',
                    minWidth: '100px'
                }}
            >
                {getDepartmentCategoryName(item.DepartmentType)}
            </td>
            <td
                style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '200px',
                    minWidth: '120px'
                }}
            >
                {item.DepartmentName}
            </td>
            <td
                style={{
                    wordWrap: 'break-word',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '180px',
                    minWidth: '100px'
                }}
            >
                {item.FunctionalAreaName}
            </td>
            <td
                className="text-center"
                style={{
                    whiteSpace: 'nowrap',
                    minWidth: '60px'
                }}
            >
                {item.Notes ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}
            </td>
            <td
                className="text-center"
                style={{
                    whiteSpace: 'nowrap',
                    minWidth: '60px'
                }}
            >
                {item.Acpm ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}
            </td>
            <td
                className="text-center"
                style={{
                    whiteSpace: 'nowrap',
                    minWidth: '50px'
                }}
            >
                {item.OpportunityCount}
            </td>
        </tr>
    );

    // Table rendering with popovers for filters
    const getTable = () => (
        <div className="table-responsive" style={{ overflowX: 'auto' }}>
            <table
                id="observerTable"
                className="table table-striped"
                style={{
                    minWidth: '900px',
                    tableLayout: 'auto'
                }}
            >
                <thead>
                    <tr>
                        {/* Id */}
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '60px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('Id', { ns: 'labels' })}
                                <button
                                    id="filter-btn-id"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-id">
                                <PopoverHeader>{t('Id', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder={t('Id', { ns: 'labels' })}
                                        value={tableIdFilter}
                                        onChange={e => setTableIdFilter(e.target.value)}
                                    />
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Data */}
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '100px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('date', { ns: 'labels' })}
                                <button
                                    id="filter-btn-date"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-date">
                                <PopoverHeader>{t('date', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm"
                                        value={tableDateFilter}
                                        onChange={e => setTableDateFilter(e.target.value)}
                                    />
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Osservatore */}
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '180px',
                                minWidth: '120px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('observer', { ns: 'labels' })}
                                <button
                                    id="filter-btn-observer"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-observer">
                                <PopoverHeader>{t('observer', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm"
                                        placeholder={t('observer', { ns: 'labels' })}
                                        value={tableObserverFilter}
                                        onChange={e => setTableObserverFilter(e.target.value)}
                                    />
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Categoria del reparto (checkbox list) */}
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '180px',
                                minWidth: '100px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('departmentCategory', { ns: 'labels' })}
                                <button
                                    id="filter-btn-deptCategory"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover
                                trigger="legacy"
                                placement="bottom"
                                target="filter-btn-deptCategory"
                            >
                                <PopoverHeader>{t('departmentCategory', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {handHygieneDepartmentsCategoryVoc.map(cat => (
                                        <div key={cat.Code} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`chk-cat-${cat.Code}`}
                                                checked={tableDeptCategoryFilter.includes(cat.Name)}
                                                onChange={() => {
                                                    const next = tableDeptCategoryFilter.includes(cat.Name)
                                                        ? tableDeptCategoryFilter.filter(x => x !== cat.Name)
                                                        : [...tableDeptCategoryFilter, cat.Name];
                                                    setTableDeptCategoryFilter(next);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`chk-cat-${cat.Code}`}>
                                                {cat.Name}
                                            </label>
                                        </div>
                                    ))}
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Reparto (checkbox list) */}
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '200px',
                                minWidth: '120px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('department', { ns: 'labels' })}
                                <button
                                    id="filter-btn-department"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover
                                trigger="legacy"
                                placement="bottom"
                                target="filter-btn-department"
                            >
                                <PopoverHeader>{t('department', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {departmentsVoc.map(dep => (
                                        <div key={dep.Id} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`chk-dep-${dep.Id}`}
                                                checked={tableDepartmentFilter.includes(dep.Name)}
                                                onChange={() => {
                                                    const next = tableDepartmentFilter.includes(dep.Name)
                                                        ? tableDepartmentFilter.filter(x => x !== dep.Name)
                                                        : [...tableDepartmentFilter, dep.Name];
                                                    setTableDepartmentFilter(next);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`chk-dep-${dep.Id}`}>
                                                {dep.Name}
                                            </label>
                                        </div>
                                    ))}
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>



                        {/* Area operativa (checkbox list) */}
                        <th
                            style={{
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                maxWidth: '180px',
                                minWidth: '100px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('functionalArea', { ns: 'labels' })}
                                <button
                                    id="filter-btn-funcArea"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover
                                trigger="legacy"
                                placement="bottom"
                                target="filter-btn-funcArea"
                            >
                                <PopoverHeader>{t('functionalArea', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    {filteredFunctionalArea.map(fa => (
                                        <div key={fa.Id} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`chk-fa-${fa.Id}`}
                                                checked={tableFunctionalAreaFilter.includes(fa.Name)}
                                                onChange={() => {
                                                    const next = tableFunctionalAreaFilter.includes(fa.Name)
                                                        ? tableFunctionalAreaFilter.filter(x => x !== fa.Name)
                                                        : [...tableFunctionalAreaFilter, fa.Name];
                                                    setTableFunctionalAreaFilter(next);
                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`chk-fa-${fa.Id}`}>
                                                {fa.Name}
                                            </label>
                                        </div>
                                    ))}
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Note */}
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '60px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('notes', { ns: 'labels' })}
                                <button
                                    id="filter-btn-notes"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-notes">
                                <PopoverHeader>{t('notes', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <select
                                        className="form-select form-select-sm"
                                        value={tableNotesFilter}
                                        onChange={e => setTableNotesFilter(e.target.value)}
                                    >
                                        <option value="">—</option>
                                        <option value="yes">{t('yes', { ns: 'labels' })}</option>
                                        <option value="no">{t('no', { ns: 'labels' })}</option>
                                    </select>
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Acpm */}
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '60px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('acpm', { ns: 'labels' })}
                                <button
                                    id="filter-btn-acpm"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-acpm">
                                <PopoverHeader>{t('acpm', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <select
                                        className="form-select form-select-sm"
                                        value={tableAcpMFilter}
                                        onChange={e => setTableAcpMFilter(e.target.value)}
                                    >
                                        <option value="">—</option>
                                        <option value="yes">{t('yes', { ns: 'labels' })}</option>
                                        <option value="no">{t('no', { ns: 'labels' })}</option>
                                    </select>
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>

                        {/* Opp */}
                        <th
                            style={{
                                whiteSpace: 'nowrap',
                                minWidth: '50px'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                {t('opp', { ns: 'labels' })}
                                <button
                                    id="filter-btn-opp"
                                    className="btn btn-link btn-sm ms-1 p-0"
                                    style={{ color: '#FFA500' }}
                                >
                                    <i className="fa fa-filter" />
                                </button>
                            </div>
                            <UncontrolledPopover trigger="legacy" placement="bottom" target="filter-btn-opp">
                                <PopoverHeader>{t('opp', { ns: 'labels' })}</PopoverHeader>
                                <PopoverBody>
                                    <input
                                        type="number"
                                        className="form-control form-control-sm"
                                        placeholder={t('opp', { ns: 'labels' })}
                                        value={tableOppFilter}
                                        onChange={e => setTableOppFilter(e.target.value)}
                                    />
                                </PopoverBody>
                            </UncontrolledPopover>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedList.map(getTableRow)}
                </tbody>
            </table>
            {/* Pagination controls */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-center px-4 py-2">
                {/* Pages row */}
                <div className="mb-2 mb-md-0">
                    <label className="me-2">{t('pageRows', { ns: 'labels' })}</label>
                    <select
                        className="form-select form-select-sm d-inline-block w-auto"
                        value={itemsPerPage}
                        onChange={e => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {[50, 100, 150].map(n => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>

                {/* Page navigation */}
                <div className="d-flex align-items-center">
                    <button
                        className="btn btn-outline-primary btn-sm me-2"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                    >
                        ‹   {t('backPage', { ns: 'labels' })}
                    </button>
                    <span className="mx-2">
                        {t('pageOf', {
                            ns: 'labels',
                            currentPage,
                            totalPages: Math.ceil(displayedList.length / itemsPerPage) || 1
                        })}
                    </span>
                    <button
                        className="btn btn-outline-primary btn-sm ms-2"
                        disabled={currentPage >= Math.ceil(displayedList.length / itemsPerPage)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                    >
                        {t('nextPage', { ns: 'labels' })} ›
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <>
            {/* Breadcrumbs managed by centralized component */}
            <div className="page-titles">
                <ol className="breadcrumb">
                </ol>
            </div>

            <div className="container-fluid">
                {/* Top filter panel */}
                <div className="row">
                    <div className="col-xl-12 col-lg-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Filter</h4>
                            </div>
                            <div className="card-body">
                                <div className="basic-form row">
                                    {/* observer */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('observer', { ns: 'labels' })}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Observer"
                                            value={observer}
                                            onChange={(e) => setObserver(e.target.value)}
                                            readOnly={!currentUser().Rights.some(right =>
                                                (right.nodeId.toLowerCase() === RightsTreeEnum.HAND_HYGIENE.toLowerCase() && right.action === "list") ||
                                                (right.nodeId.toLowerCase() === RightsTreeEnum.ROOT.toLowerCase() && right.action === "list")
                                            )}
                                        />
                                    </div>
                                    {/* reparto */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('department', { ns: 'labels' })}</label>
                                        <Choices
                                            ref={departmentRef}
                                            voc={departmentsVoc}
                                            value="Id"
                                            text="Name"
                                            multiple="true"
                                            className="inputStyle orange-select blackText"
                                            selected={department.map(id => ({ Id: id }))}
                                            onChange={(e) => {
                                                const sel = e.selected || [];
                                                setDepartment(sel.map(x => x.Id));
                                                if (sel.length) {
                                                    let all = [];
                                                    sel.forEach(s => {
                                                        all.push(...getDescendants(functionalAreaVoc, s.Id));
                                                    });
                                                    setFilteredFunctionalArea(all);
                                                } else {
                                                    setFilteredFunctionalArea(functionalAreaVoc);
                                                }
                                            }}
                                        />
                                    </div>
                                    {/* categoria reparto */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('departmentCategory', { ns: 'labels' })}</label>
                                        <Choices
                                            ref={departmentCategoryRef}
                                            voc={handHygieneDepartmentsCategoryVoc}
                                            value="Code"
                                            text="Name"
                                            multiple="true"
                                            className="inputStyle orange-select blackText"
                                            selected={departmentCategory.length > 0
                                                ? departmentCategory.map(code => ({ Code: code }))
                                                : []}
                                            onChange={(e) => {
                                                const selected = e.selected || [];
                                                setDepartmentCategory(selected.map(x => x.Code));
                                            }}
                                        />
                                    </div>
                                    {/* area operativa */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('functionalArea', { ns: 'labels' })}</label>
                                        <Choices
                                            ref={functionalAreaRef}
                                            voc={filteredFunctionalArea}
                                            value="Id"
                                            text="Name"
                                            multiple="true"
                                            className="inputStyle orange-select blackText"
                                            selected={functionalArea.map(id => ({ Id: id }))}
                                            onChange={(e) => setFunctionalArea((e.selected || []).map(x => x.Id))}
                                        />
                                    </div>
                                    {/* date inizio */}
                                    <div className="mb-3 col-md-3">
                                        <label className="form-label">{t('startDate', { ns: 'labels' })}</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={startObservationDate}
                                            onChange={e => setStartObservationDate(e.target.value)}
                                        />
                                    </div>
                                    {/* date fine */}
                                    <div className="mb-3 col-md-3">
                                        <label className="form-label">{t('endDate', { ns: 'labels' })}</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={endObservationDate}
                                            onChange={e => setEndObservationDate(e.target.value)}
                                        />
                                    </div>
                                    {/* id */}
                                    <div className="mb-3 col-md-6">
                                        <label className="form-label">{t('id', { ns: 'labels' })}</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={indexId}
                                            onChange={e => setIndexId(e.target.value)}
                                        />
                                    </div>
                                    {/* bottoni */}
                                    <div className="mb-3 col-md-12">
                                        <button type="button" className="btn btn-primary" onClick={getFilteredData} aria-label={t('search', { ns: 'labels' })}>
                                            {t('search', { ns: 'labels' })}
                                        </button>
                                        <button type="button" className="btn btn-secondary ms-2" onClick={clearData} aria-label={t('clear', { ns: 'labels' })}>
                                            {t('clear', { ns: 'labels' })}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/*Filtered Data Table */}
                {searchList && (
                    <div className="row">
                        <div className="col-xl-12 col-lg-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div className="active-projects task-table">
                                        <div className="tbl-caption d-flex justify-content-between align-items-center px-4 py-2">
                                            <h4 className="heading mb-0">{t('findedElements', { ns: 'labels' })}: {displayedList.length}</h4>
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={clearTableFilters}
                                            >
                                                {t('clear', { ns: 'labels' })}
                                            </button>
                                        </div>

                                        {getTable()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}

export { Hand_Hygiene_Finder };
